/**
 * Wizard Progress Indicator Component
 *
 * Displays the current step in the signup wizard with visual progress.
 * Implements accessibility requirements with aria-current and aria-label.
 *
 * WP-AUTH-11: Multi-Step Signup Wizard
 */

import { cn } from '@/utils/cn';

interface Step {
  number: 1 | 2 | 3;
  name: string;
  shortName: string;
}

const STEPS: Step[] = [
  { number: 1, name: 'Terms & Privacy', shortName: 'Terms' },
  { number: 2, name: 'Sign-Up Method', shortName: 'Method' },
  { number: 3, name: 'Create Account', shortName: 'Details' },
];

export interface WizardProgressProps {
  /** Current step (1, 2, or 3) */
  currentStep: 1 | 2 | 3;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Check icon for completed steps
 */
function CheckIcon() {
  return (
    <svg
      className="w-4 h-4"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Wizard Progress Component
 *
 * Shows:
 * - Step number text: "Step X of 3"
 * - Visual step indicators with completed/current/upcoming states
 */
export default function WizardProgress({
  currentStep,
  className,
}: WizardProgressProps) {
  return (
    <nav
      aria-label="Signup progress"
      className={cn('w-full', className)}
    >
      {/* Step indicator text */}
      <div className="text-center mb-4">
        <p className="text-sm font-medium text-gray-600">
          Step {currentStep} of {STEPS.length}
        </p>
      </div>

      {/* Skip link for screen readers */}
      <a
        href="#wizard-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-10 focus:bg-white focus:p-2 focus:text-primary-600 focus:underline"
      >
        Skip to wizard content
      </a>

      {/* Visual progress indicator */}
      <ol className="flex items-center justify-center" role="list">
        {STEPS.map((step, index) => {
          const isCompleted = step.number < currentStep;
          const isCurrent = step.number === currentStep;
          const isUpcoming = step.number > currentStep;

          return (
            <li
              key={step.number}
              className="flex items-center"
              aria-current={isCurrent ? 'step' : undefined}
            >
              {/* Connector line (before step, except first) */}
              {index > 0 && (
                <div
                  className={cn(
                    'w-12 sm:w-16 h-0.5 mx-1',
                    isCompleted || isCurrent ? 'bg-primary-600' : 'bg-gray-200'
                  )}
                  aria-hidden="true"
                />
              )}

              {/* Step indicator */}
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors',
                    isCompleted && 'bg-primary-600 text-white',
                    isCurrent &&
                      'bg-primary-600 text-white ring-2 ring-primary-600 ring-offset-2',
                    isUpcoming && 'bg-gray-200 text-gray-600'
                  )}
                  aria-label={
                    isCompleted
                      ? `Step ${step.number}: ${step.name} - completed`
                      : isCurrent
                        ? `Step ${step.number}: ${step.name} - current`
                        : `Step ${step.number}: ${step.name} - upcoming`
                  }
                >
                  {isCompleted ? <CheckIcon /> : step.number}
                </div>

                {/* Step name (hidden on mobile, visible on larger screens) */}
                <span
                  className={cn(
                    'mt-1 text-xs hidden sm:block',
                    isCompleted && 'text-primary-600 font-medium',
                    isCurrent && 'text-primary-600 font-semibold',
                    isUpcoming && 'text-gray-500'
                  )}
                >
                  {step.shortName}
                </span>
              </div>
            </li>
          );
        })}
      </ol>

      {/* Current step name for mobile */}
      <div className="text-center mt-2 sm:hidden">
        <p className="text-sm font-medium text-gray-700">
          {STEPS.find((s) => s.number === currentStep)?.name}
        </p>
      </div>
    </nav>
  );
}
